import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo } from 'react';

import type { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';

import type {
  UserCardsSearchFacetValuesQuery,
  UserCardsSearchFacetValuesQueryVariables,
  UserCardsSearchQuery,
  UserCardsSearchQueryVariables,
} from './__generated__/useUserCardsSearchClient.graphql.ts';
import { useCardsSearchClient } from './useCardsSearchClient.ts';

export const USER_CARDS_SEARCH_FACET_VALUES_QUERY = gql`
  query UserCardsSearchFacetValuesQuery(
    $userSlug: String!
    $rarity: Rarity
    $sport: Sport!
    $field: String!
    $facetQuery: String!
    $query: String!
    $limit: Int
    $advancedFilters: String
    $refinements: [SearchRefinementInput!]!
    $cardSetSlug: String
  ) {
    user(slug: $userSlug) {
      slug
      searchCardFacetValues(
        field: $field
        facetQuery: $facetQuery
        query: $query
        limit: $limit
        rarity: $rarity
        sport: $sport
        advancedFilters: $advancedFilters
        refinements: $refinements
        cardSetSlug: $cardSetSlug
      ) {
        ...useCardsSearchClient_searchCardFacetValues
      }
    }
  }
  ${useCardsSearchClient.fragments.searchCardFacetValues}
` as TypedDocumentNode<
  UserCardsSearchFacetValuesQuery,
  UserCardsSearchFacetValuesQueryVariables
>;

export const USER_CARDS_SEARCH_QUERY = gql`
  query UserCardsSearchQuery(
    $userSlug: String!
    $rarity: Rarity
    $sport: Sport!
    $query: String!
    $page: Int
    $pageSize: Int
    $duplicatedKey: String
    $advancedFilters: String
    $sorts: [SearchSortInput!]!
    $refinements: [SearchRefinementInput!]!
    $facets: [SearchFacetDefinitionInput!]!
    $cardSetSlug: String
  ) {
    user(slug: $userSlug) {
      slug
      searchCards(
        query: $query
        page: $page
        pageSize: $pageSize
        sorts: $sorts
        sport: $sport
        rarity: $rarity
        duplicatedKey: $duplicatedKey
        advancedFilters: $advancedFilters
        refinements: $refinements
        facets: $facets
        cardSetSlug: $cardSetSlug
      ) {
        hits {
          slug
          blueprint {
            id
            revealStatus
          }
        }
        ...useCardsSearchClient_searchCards
      }
    }
  }
  ${useCardsSearchClient.fragments.searchCards}
` as TypedDocumentNode<UserCardsSearchQuery, UserCardsSearchQueryVariables>;

export const useUserCardsSearchClient = (
  userSlug: string,
  {
    rarity,
    sport,
    sealed,
    duplicatedKey,
    cardSetSlug,
  }: {
    rarity: Rarity | undefined;
    sport: Sport;
    sealed?: boolean;
    duplicatedKey?: string;
    cardSetSlug?: string | null;
  }
) => {
  const {
    flags: { useNetworkOnlySearchClient = true },
  } = useFeatureFlags();

  const options = useMemo(
    () => ({
      userSlug,
      rarity,
      sport,
      sealed,
      duplicatedKey,
      cardSetSlug,
      fetchPolicy: useNetworkOnlySearchClient
        ? ('network-only' as const)
        : undefined,
    }),
    [
      userSlug,
      rarity,
      sport,
      sealed,
      useNetworkOnlySearchClient,
      duplicatedKey,
      cardSetSlug,
    ]
  );

  return useCardsSearchClient<
    typeof USER_CARDS_SEARCH_QUERY,
    typeof USER_CARDS_SEARCH_FACET_VALUES_QUERY
  >(USER_CARDS_SEARCH_QUERY, USER_CARDS_SEARCH_FACET_VALUES_QUERY, options);
};
